import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { useGetDashboardLayoutQuery } from "@/services/(company)/api.dashboard-layout";
import type { TDashboardScope } from "@/types/(company)/dashboardLayout.interface";
import { ARRIVAL_CUE_MS } from "./grid.constants";
import {
  filterByPermission,
  makeInstanceId,
  sanitizePlacements,
} from "./grid.utils";
import {
  INITIAL_LAYOUT_STATE,
  createLayoutReducer,
  isLayoutDirty,
} from "./layout.reducer";
import { showUndoToast } from "./show-undo-toast";
import { useLayoutPersistence } from "./use-layout-persistence";
import { useViewTransition } from "./use-view-transition";
import { useWidgetRegistry } from "./use-widget-registry";
import type {
  IWidgetDefinition,
  IWidgetPlacement,
  TWidgetSizeKey,
} from "./types";

interface IUseDashboardLayoutArgs {
  scope: TDashboardScope;
  /** The dashboard's full widget catalogue. */
  registry: IWidgetDefinition[];
  /** Arrangement used until the user saves one of their own. */
  defaultLayout: IWidgetPlacement[];
}

/**
 * Composes the canvas: catalogue + permissions, local state machine, server sync.
 *
 * Deliberately thin. It holds no logic of its own beyond turning a user gesture
 * into an action — the transitions live in `layout.reducer`, the writes in
 * `use-layout-persistence`, the permission resolution in `use-widget-registry`.
 * Each of those is readable and testable on its own, which the single 380-line
 * hook this replaced was not.
 *
 * Two lists come out. `placements` is everything the user has arranged and is
 * what gets saved; `visiblePlacements` is that list minus what their permissions
 * currently hide, and is what gets rendered. Keeping them separate is what lets a
 * widget survive a temporary loss of access instead of being deleted the next
 * time the user reorders a card.
 */
export function useDashboardLayout({
  scope,
  registry,
  defaultLayout,
}: IUseDashboardLayoutArgs) {
  const {
    definitions,
    allowedTypes,
    availableWidgets,
    isLoading: isPermissionsLoading,
  } = useWidgetRegistry(registry);

  const { data, isLoading: isLayoutLoading } = useGetDashboardLayoutQuery({
    scope,
  });

  const reducer = useMemo(
    () => createLayoutReducer(definitions),
    [definitions],
  );
  const [state, dispatch] = useReducer(reducer, INITIAL_LAYOUT_STATE);

  /**
   * Dispatch, with the grid re-flow animated.
   *
   * Used for the edits that move cards the user did not touch — resize, remove,
   * restore, reset. Deliberately NOT used for `add` (the new card appends, so
   * nothing else moves, and it has its own entrance) or for `move` (dnd-kit
   * already owns that animation and the two would fight).
   */
  const startViewTransition = useViewTransition();
  const dispatchAnimated = useCallback(
    (action: Parameters<typeof dispatch>[0]) =>
      startViewTransition(() => dispatch(action)),
    [startViewTransition],
  );

  const isDirty = isLayoutDirty(state);
  const isLoading = isLayoutLoading || isPermissionsLoading;

  // ── Hydration ────────────────────────────────────────────────────────────
  // Seed once, from the saved layout when there is one and from the registry
  // default otherwise. Deliberately not re-seeded on later query results: a
  // refetch landing mid-edit would throw away the user's changes.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (hasHydratedRef.current || isLoading) return;
    hasHydratedRef.current = true;
    dispatch({
      type: "hydrate",
      placements: (data?.data?.layout?.widgets ??
        defaultLayout) as IWidgetPlacement[],
      // Straight from the server: it is the only thing that knows whether a row
      // exists for this person. A failed request leaves this false, which is
      // correct — the canvas is showing the default in that case too.
      isCustomized: data?.data?.isCustomized ?? false,
      source: data?.data?.source ?? "fallback",
    });
  }, [data, defaultLayout, isLoading]);

  const handlePersisted = useCallback((placements: IWidgetPlacement[]) => {
    dispatch({ type: "markSaved", placements });
  }, []);

  const { saveState, flush, resetRemote } = useLayoutPersistence({
    scope,
    placements: state.placements,
    isDirty,
    onPersisted: handlePersisted,
  });

  /**
   * What actually reaches the canvas. Derived rather than stored, so a permission
   * resolving after hydration changes what renders without touching the
   * arrangement that gets saved.
   */
  const visiblePlacements = useMemo(
    () => filterByPermission(state.placements ?? [], allowedTypes),
    [state.placements, allowedTypes],
  );

  // ── Gestures ─────────────────────────────────────────────────────────────
  const addWidget = useCallback(
    (type: string) => {
      const definition = definitions.get(type);
      if (!definition || !allowedTypes.has(type)) return;

      // Built here, not in the reducer: ids are random, and a reducer must be a
      // pure function of its inputs.
      dispatch({
        type: "add",
        placement: {
          instanceId: makeInstanceId(type),
          type,
          size: definition.defaultSize,
        },
      });
    },
    [allowedTypes, definitions],
  );

  /** Clears the arrival cue once its animation has had time to play. */
  useEffect(() => {
    if (!state.arrivingId) return;
    const timer = setTimeout(
      () => dispatch({ type: "clearArrival" }),
      ARRIVAL_CUE_MS,
    );
    return () => clearTimeout(timer);
  }, [state.arrivingId]);

  // Read inside gesture handlers that need the list as it is *now* without
  // taking a dependency on it — keeps every callback below stable, which is what
  // lets the memoised widget frames stay un-rendered during a drag.
  const placementsRef = useRef(state.placements);
  placementsRef.current = state.placements;

  const removeWidget = useCallback(
    (instanceId: string) => {
      const current = placementsRef.current;
      const index =
        current?.findIndex(
          (placement) => placement.instanceId === instanceId,
        ) ?? -1;
      if (!current || index < 0) return;

      const removed = current[index];
      const name = definitions.get(removed.type)?.name ?? "Widget";

      dispatchAnimated({ type: "remove", instanceId });

      // Restores the exact placement at its exact index — re-adding from the
      // library would come back at the default size and at the end of the list.
      showUndoToast(`${name} removed`, () =>
        dispatchAnimated({ type: "restore", placement: removed, index }),
      );
    },
    [definitions, dispatchAnimated],
  );

  const resizeWidget = useCallback(
    (instanceId: string, size: TWidgetSizeKey) => {
      dispatchAnimated({ type: "resize", instanceId, size });
    },
    [dispatchAnimated],
  );

  /** Reorder by instance id — the grid never deals in indices. */
  const moveWidget = useCallback((activeId: string, overId: string) => {
    dispatch({ type: "move", activeId, overId });
  }, []);

  /** Drop the caller's own row and fall back to the client's registry default. */
  const resetLayout = useCallback(async () => {
    const previous = placementsRef.current;
    const fallback = sanitizePlacements(defaultLayout, definitions);

    // One transition, placements and saved together: the canvas is never dirty
    // while the DELETE is in flight, so the save debounce cannot fire and PUT
    // the default straight back into the row being deleted.
    dispatchAnimated({
      type: "reset",
      placements: fallback,
      source: "fallback",
    });

    await resetRemote();

    // Reset is a bigger swing than a remove, so it gets the same escape hatch.
    if (previous?.length) {
      showUndoToast("Dashboard reset", () =>
        dispatchAnimated({ type: "replace", placements: previous }),
      );
    }
  }, [defaultLayout, definitions, dispatchAnimated, resetRemote]);

  return {
    /** Everything the user has arranged — the list that gets saved. */
    placements: state.placements ?? [],
    /** The subset their permissions allow — the list that gets rendered. */
    visiblePlacements,
    isLoading: isLoading || state.placements === null,
    availableWidgets,
    definitions,
    saveState,
    /** True once this person has an arrangement of their own on the server. */
    isCustomized: state.isCustomized,
    /** Which tier the current arrangement came from — see ILayoutState.source. */
    source: state.source,
    arrivingId: state.arrivingId,
    addWidget,
    removeWidget,
    resizeWidget,
    moveWidget,
    resetLayout,
    flush,
  };
}
